import type { Label, Rawdata, WidgetObject } from '@/types';
import { AnnoFactory, AnnoInstanceItem, IAnnoItem } from '@/utils/annos';

export interface IAnnotation {
  // 当前帧的标注物集合
  annotations: Record<IAnnoItem['name'], IAnnoItem | null>;
  // 标签与标注物的映射
  labelAnnoMap: Record<Label['name'], Array<Pick<IAnnoItem, 'name' | 'trackId'>>>;
  // 当前帧中各文件与标注物的映射, 取 Rawdata.name 作为 key
  fileAnnoMap: Record<Rawdata['name'], Array<IAnnoItem['name']>>;
}

export class AnnoManager implements IAnnotation {
  public annotations;
  public labelAnnoMap;
  public fileAnnoMap;

  constructor(annos?: IAnnotation | null) {
    const { annotations = {}, labelAnnoMap = {}, fileAnnoMap = {} } = annos || {};
    this.annotations = annotations;
    this.labelAnnoMap = labelAnnoMap;
    this.fileAnnoMap = fileAnnoMap;
  }

  /**
   * 将传入的 annotations 中的 json object 数据转换为类数据，会改变传入的 annotations 数据
   * @param annotations
   * @param onCreate
   * @returns
   */
  static transInAnnoClass(
    annotations: Record<IAnnoItem['name'], IAnnoItem | null>,
    onCreate?: (item: IAnnoItem | null) => void
  ) {
    for (const anno in annotations) {
      const data = annotations[anno];
      if (data) {
        // 兼容之前保存的数据格式。2024年10月5日之后可以删除这个 if 块。
        if (typeof data.label === 'object') {
          const { name } = data.label;
          if (typeof name === 'string') {
            data.label = name;
          }
        }

        annotations[anno] = AnnoFactory.createFromObject(data);
      } else {
        annotations[anno] = null;
      }
      onCreate?.(annotations[anno]);
    }
    return annotations;
  }

  static diffAnnoList(
    target: AnnoInstanceItem,
    currentItems: Record<Rawdata['name'], AnnoInstanceItem | undefined>,
    newWidgets: Array<{ widget: WidgetObject; fileName: string }>
  ) {
    const toAdd: { fileName: string; item: AnnoInstanceItem }[] = [];
    const toRemove: { fileName: string; item: AnnoInstanceItem }[] = [];
    const visited: Map<string, boolean> = new Map();

    newWidgets.forEach(({ fileName, widget }) => {
      const rect = currentItems[fileName];

      // 判断对应的映射对象是否存在
      if (rect) {
        // 如已存在映射对象，且保持着关联，则更新其数据
        if (rect.source === 'projection' && rect.widget.name === widget.name) {
          rect.widget.data = widget.data;
        }
        // 标识为已遍历
        visited.set(rect.name, true);
      } else {
        // 如不存在，则新建映射对象
        toAdd.push({
          fileName,
          item: AnnoInstanceItem.createFromMapped(target, widget),
        });
      }
    });

    for (const fileName in currentItems) {
      if (
        currentItems[fileName] &&
        AnnoFactory.isMappedType(currentItems[fileName]!.widget.name) &&
        !visited.get(currentItems[fileName]!.name)
      ) {
        // 删除未被遍历过的映射对象
        toRemove.push({ fileName, item: currentItems[fileName]! });
      }
    }

    return { toAdd, toRemove };
  }

  getAnnotations() {
    const { annotations, labelAnnoMap, fileAnnoMap } = this;
    return { annotations, labelAnnoMap, fileAnnoMap };
  }

  getAnnoInstanceItem(name?: string) {
    const anno = name ? this.annotations[name] : null;
    return anno instanceof AnnoInstanceItem ? anno : null;
  }

  setAnnotations(annos: IAnnotation) {
    const { annotations = {}, labelAnnoMap = {}, fileAnnoMap = {} } = annos || {};
    this.annotations = annotations;
    this.labelAnnoMap = labelAnnoMap;
    this.fileAnnoMap = fileAnnoMap;
  }

  getAnnoItem(name?: AnnoInstanceItem['name']) {
    return name ? this.annotations[name] ?? null : null;
  }

  getAnnosByFile(fileName: string) {
    const { fileAnnoMap, annotations } = this;
    const list: Array<AnnoInstanceItem> = [];
    if (Array.isArray(fileAnnoMap[fileName])) {
      fileAnnoMap[fileName].forEach((annoName) => {
        const anno = Object.hasOwn(annotations, annoName) && annotations[annoName];
        if (anno instanceof AnnoInstanceItem) {
          list.push(anno);
        }
      });
    }
    return list;
  }

  getAnnosByTrackId(trackId: string) {
    const { fileAnnoMap, annotations } = this;
    const annos: Record<Rawdata['name'], IAnnoItem> = {};

    for (const fileName in fileAnnoMap) {
      if (Array.isArray(fileAnnoMap[fileName])) {
        const annoName = fileAnnoMap[fileName].find((annoName) => {
          if (annotations[annoName]?.trackId === trackId) {
            return true;
          }
          return false;
        });

        if (annoName) {
          const anno = annotations[annoName];
          anno && (annos[fileName] = anno);
        }
      }
    }
    if (Object.keys(annos).length === 0) {
      return undefined;
    }
    return annos;
  }

  /**
   * 添加一个标注物，不会改变 annotations，fileAnnoMap，fileAnnoMap 的索引
   * @param obj
   * @param rawDataName
   */
  addAnnoItem(anno: IAnnoItem, rawDataName: string) {
    this.annotations[anno.name] = anno;

    if (anno.label) {
      this.labelAnnoMap[anno.label] = (this.labelAnnoMap[anno.label] || []).concat([
        {
          name: anno.name,
          trackId: anno.trackId,
        },
      ]);
    }

    this.fileAnnoMap[rawDataName] = (this.fileAnnoMap[rawDataName] || []).concat([anno.name]);
  }

  /**
   * 更新一个标注物，不会改变 annotations，fileAnnoMap，fileAnnoMap 的索引
   * @param obj
   */
  updateAnnoItem(anno: IAnnoItem) {
    this.annotations[anno.name] = anno;
  }

  /**
   * 删除一个标注物，不会改变 annotations，fileAnnoMap，fileAnnoMap 的索引
   * @param obj
   * @param rawDataName
   */
  removeAnnoItem(anno: IAnnoItem, rawDataName: string) {
    const { annotations, labelAnnoMap, fileAnnoMap } = this;

    const { name, label } = anno;

    if (Object.hasOwn(annotations, name) && annotations[name]) {
      this.annotations[name] = null;

      if (label) {
        const labelName = label;
        const labelIndex = labelAnnoMap[labelName]?.findIndex((o) => o.name === name) ?? -1;

        const newLabelAnnos =
          labelIndex < 0
            ? labelAnnoMap[labelName]
            : labelAnnoMap[labelName].slice(0, labelIndex).concat(labelAnnoMap[labelName].slice(labelIndex + 1));
        this.labelAnnoMap[labelName] = newLabelAnnos;
      }
      const fileIndex = fileAnnoMap[rawDataName]?.findIndex((aid) => aid === name) ?? -1;

      const newFileAnnos =
        fileIndex < 0
          ? fileAnnoMap[rawDataName]
          : fileAnnoMap[rawDataName].slice(0, fileIndex).concat(fileAnnoMap[rawDataName].slice(fileIndex + 1));

      this.fileAnnoMap[rawDataName] = newFileAnnos;
    }
  }

  /**
   * 更新 labelMap
   * @param annoNameList
   * @param oldLabelName
   * @param newLabelName
   */
  updateAnnosInLabelMap(
    annoNameList: Array<IAnnoItem['name']>,
    oldLabelName: Label['name'],
    newLabelName: Label['name']
  ) {
    this.labelAnnoMap[oldLabelName] = this.labelAnnoMap[oldLabelName].filter((o) => !annoNameList.includes(o.name));

    const newList = [];
    for (let i = 0; i < annoNameList.length; i++) {
      const anno = this.annotations[annoNameList[i]];
      anno && newList.push({ name: anno.name, trackId: anno.trackId });
    }

    this.labelAnnoMap[newLabelName] = (this.labelAnnoMap[newLabelName] || []).concat(newList);
  }

  /**
   * 更新 trackId
   * @param trackId
   * @param newTrackId
   */
  updateTrackIdInLabelMap(labelName: Label['name'], trackId: string, newTrackId: string) {
    const index = this.labelAnnoMap[labelName].findIndex((o) => o.trackId === trackId);
    if (index !== -1) {
      this.labelAnnoMap[labelName][index].trackId = newTrackId;
    }
  }

  /**
   * 将传入点的 IAnnotation 数据与自身数据做合并
   * @param data IAnnotation
   */
  importAnnotations(data: IAnnotation) {
    const { annotations: newAnnos, fileAnnoMap } = data;

    const newList: Array<{ item: IAnnoItem; fileName: string }> = [];

    for (const fileName in fileAnnoMap) {
      if (Array.isArray(fileAnnoMap[fileName])) {
        fileAnnoMap[fileName].forEach((name) => {
          const anno = newAnnos[name];
          anno && newList.push({ item: anno, fileName });
        });
      }
    }

    this.addAnnoObjectList(newList);
  }

  addAnnoObjectList(list: Array<{ item: IAnnoItem; fileName: string }>) {
    if (list.length === 0) return;

    list.forEach(({ item, fileName }) => {
      // 判断是否已有同 name 的标注物
      if (!this.annotations[item.name]) {
        this.annotations[item.name] = item;

        if (item.label) {
          if (Array.isArray(this.labelAnnoMap[item.label])) {
            this.labelAnnoMap[item.label].push({
              name: item.name,
              trackId: item.trackId,
            });
          } else {
            this.labelAnnoMap[item.label] = [
              {
                name: item.name,
                trackId: item.trackId,
              },
            ];
          }
        }

        if (Array.isArray(this.fileAnnoMap[fileName])) {
          this.fileAnnoMap[fileName].push(item.name);
        } else {
          this.fileAnnoMap[fileName] = [item.name];
        }
      }
    });
  }
  removeAnnoObjectList(list: Array<{ item: IAnnoItem; fileName: string }>) {
    if (list.length === 0) return;

    list.forEach(({ item, fileName }) => {
      this.annotations[item.name] = null;

      if (item.label) {
        if (Array.isArray(this.labelAnnoMap[item.label])) {
          const arr = this.labelAnnoMap[item.label];
          const index = arr.findIndex(({ name }) => name === item.name);

          if (index >= 0) {
            this.labelAnnoMap[item.label] = arr.slice(0, index).concat(arr.slice(index + 1));
          }
        }
      }

      if (Array.isArray(this.fileAnnoMap[fileName])) {
        const arr = this.fileAnnoMap[fileName];
        const index = arr.findIndex((name) => name === item.name);

        if (index >= 0) {
          this.fileAnnoMap[fileName] = arr.slice(0, index).concat(arr.slice(index + 1));
        }
      }
    });
  }
}
